import React, { useContext } from "react";
import { Modal, Input, Row, Col, Checkbox } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import useAxiosInstance from "../../lib/useAxiosInstance";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import ReportContext from "../../context/reportContext";

const AddMedicalCamp = ({ isOpen }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const medicalCampContext = useContext(ReportContext);

  const formik = useFormik({
    initialValues: {
      name: "",
      fatherOrHusbandName: "",
      dob: "",
      age: "",
      address: "",
      totalAdults: "",
      contactNo: "",
      idNo: "",
      consent: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      fatherOrHusbandName: Yup.string().required("Husband/Father Name is required"),
      dob: Yup.date().required("DOB is required"),
      age: Yup.number().required("Age is required"),
      address: Yup.string().required("Address is required"),
      totalAdults: Yup.number().required("Total Adults is required"),
      contactNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits")
        .required("Contact No is required"),
      idNo: Yup.string().required("Identification Number is required"),
      consent: Yup.boolean().oneOf([true], "Consent is required"),
    }),
    onSubmit: (values) => {
      createMedicalCamp.mutate(values);
    },
  });

  const createMedicalCamp = useMutation(
    (data) => axiosInstance.post("api/medicalCamp", data),
    {
      onSuccess: (res) => {
        toast.success(res.data.message || "Medical Camp added");
        queryClient.invalidateQueries("medicalCamp");
        closeModal();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Error adding camp");
      },
    }
  );

  const closeModal = () => {
    medicalCampContext.updateOpenModal(false);
    formik.resetForm();
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleDobChange = (e) => {
    const dobValue = e.target.value;
    formik.setFieldValue("dob", dobValue);
    const age = calculateAge(dobValue);
    formik.setFieldValue("age", age);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={closeModal}
      onOk={formik.handleSubmit}
      title="Add Medical Camp Records"
      okText="Add"
      destroyOnClose
      okButtonProps={{
        disabled: !formik.values.consent,
        style: {
          backgroundColor: !formik.values.consent ? "grey" : "",
          color: "white",
          borderColor: !formik.values.consent ? "grey" : "",
          cursor: !formik.values.consent ? "not-allowed" : "pointer",
          marginRight:"20px"
        },
      }}
    >
      <form onSubmit={formik.handleSubmit} className="p-6">
        <Row gutter={16} className="mb-3">
          <Col span={12}>
            <label>Name</label>
            <Input name="name" value={formik.values.name} onChange={formik.handleChange} />
            {formik.touched.name && formik.errors.name && (
              <div className="text-red-500 text-sm">{formik.errors.name}</div>
            )}
          </Col>
          <Col span={12}>
            <label>Husband / Father Name</label>
            <Input
              name="fatherOrHusbandName"
              value={formik.values.fatherOrHusbandName}
              onChange={formik.handleChange}
            />
            {formik.touched.fatherOrHusbandName && formik.errors.fatherOrHusbandName && (
              <div className="text-red-500 text-sm">{formik.errors.fatherOrHusbandName}</div>
            )}
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={12}>
            <label>DOB</label>
            <Input type="date" name="dob" value={formik.values.dob} onChange={handleDobChange} />
            {formik.touched.dob && formik.errors.dob && (
              <div className="text-red-500 text-sm">{formik.errors.dob}</div>
            )}
          </Col>
          <Col span={12}>
            <label>Age</label>
            <Input name="age" value={formik.values.age} disabled />
            {formik.touched.age && formik.errors.age && (
              <div className="text-red-500 text-sm">{formik.errors.age}</div>
            )}
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={24}>
            <label>Address</label>
            <Input name="address" value={formik.values.address} onChange={formik.handleChange} />
            {formik.touched.address && formik.errors.address && (
              <div className="text-red-500 text-sm">{formik.errors.address}</div>
            )}
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={12}>
            <label>Total Adults in Family</label>
            <Input
              name="totalAdults"
              value={formik.values.totalAdults}
              onChange={formik.handleChange}
            />
            {formik.touched.totalAdults && formik.errors.totalAdults && (
              <div className="text-red-500 text-sm">{formik.errors.totalAdults}</div>
            )}
          </Col>
          <Col span={12}>
            <label>Contact No / WhatsApp No</label>
            <Input
              name="contactNo"
              value={formik.values.contactNo}
              onChange={formik.handleChange}
            />
            {formik.touched.contactNo && formik.errors.contactNo && (
              <div className="text-red-500 text-sm">{formik.errors.contactNo}</div>
            )}
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={24}>
            <label>Identification Number</label>
            <Input name="idNo" value={formik.values.idNo} onChange={formik.handleChange} />
            {formik.touched.idNo && formik.errors.idNo && (
              <div className="text-red-500 text-sm">{formik.errors.idNo}</div>
            )}
          </Col>
        </Row>

        <Row gutter={16} className="mb-3 mt-9">
          <Col span={24}>
            <Checkbox
              name="consent"
              checked={formik.values.consent}
              onChange={(e) => formik.setFieldValue("consent", e.target.checked)}
            >
              <span className="text-xs">I consent to the collection of my information for the stated purpose</span>
            </Checkbox>
            {formik.touched.consent && formik.errors.consent && (
              <div className="text-red-500 text-sm">{formik.errors.consent}</div>
            )}
          </Col>
        </Row>
      </form>
    </Modal>
  );
};

export default AddMedicalCamp;
